import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@material-ui/core";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { makeStyles } from "@material-ui/styles";
import * as ChartAnnotation from "chartjs-plugin-annotation";
import * as ChartZoom from "chartjs-plugin-zoom";
import _ from "lodash";
import moment from "moment";
import pattern from "patternomaly";
import PropTypes from "prop-types";
import React, { createRef, useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import ButtonConfirm from "src/components/ButtonConfirm";
import MaintenanceOnboarding from "src/components/Maintenance/MaintenanceOnboarding";
import palette from "src/theme/palette";
import { isOEMSuperUser, isSuperUser } from "src/utils/session";
import { useTranslation } from "react-i18next";
import { getWebhooks } from "src/repos/webhooks";
import { updateMaintenance } from "src/repos/maintenances";
import Autocomplete from "@material-ui/lab/Autocomplete";

const useStyles = makeStyles((theme) => ({
  root: {},
  card: { padding: "24px 32px" },
  divider: {
    margin: "24px 0",
  },
  marginBottom: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
    alignItems: "flex-start",
  },
  flex: { display: "flex" },
  form: {
    marginLeft: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "center",
  },
  gutterRight: {
    marginRight: theme.spacing(3),
  },
  chartContainer: {
    height: "38vh",
  },
  historyContainer: {
    marginTop: 75,
  },
}));

function MaintenanceDetail(props) {
  const classes = useStyles();
  const details = props.details;
  const chartRef = createRef();
  const status = props.getMaintenanceStatus(props.details);
  const [editWebhooksOpen, setEditWebhooksOpen] = useState(false);
  const [webhooksList, setWebhooksList] = useState([]);
  const [selectedWebhooks, setSelectedWebhooks] = useState([]);
  const [webhookPayloadValues, setWebhookPayloadValues] = useState("");
  const [jsonError, setJsonError] = useState("");
  const { t } = useTranslation(["glossary", "common"]);

  useEffect(() => {
    if (chartRef?.current?.chartInstance?.$zoom) {
      chartRef.current.chartInstance.$zoom._originalOptions = {};
    }
  }, [details, chartRef]);

  useEffect(() => {
    const customerId = props.details?.customer_id;
    if (customerId) {
      getWebhooks({ customer_id: customerId })
        .then((r) => r.json())
        .then(({ data }) => setWebhooksList(data || []));
    } else {
      getWebhooks()
        .then((r) => r.json())
        .then(({ data }) => setWebhooksList(data || []));
    }
  }, [props.details]);

  // Load existing webhook payload into the editor when modal opens
  useEffect(() => {
    if (editWebhooksOpen && details?.webhook_payload_values) {
      try {
        setWebhookPayloadValues(JSON.stringify(details.webhook_payload_values, null, 2));
        setJsonError("");
      } catch (e) {
        setWebhookPayloadValues("");
      }
    } else if (editWebhooksOpen) {
      setWebhookPayloadValues("");
    }
  }, [editWebhooksOpen, details]);

  // Preselect webhooks when modal opens
  useEffect(() => {
    if (editWebhooksOpen && details?.webhooks) {
      setSelectedWebhooks(details.webhooks);
    } else if (editWebhooksOpen) {
      setSelectedWebhooks([]);
    }
  }, [editWebhooksOpen, details]);

  const estimationData = [
    {
      x: details.burn_rates?.[0]?.system_time,
      y: details.burn_rates?.[0]?.value,
    },
    { x: details.recommended_date, y: details.cutoff_value },
  ];

  const chartData = details.burn_rates
    ? details.burn_rates.map((rate) => ({ x: rate.system_time, y: rate.value }))
    : {};

  const data = {
    datasets: [
      {
        label: t("burndown"),
        fill: false,
        lineTension: 0.5,
        backgroundColor: palette.primary.main,
        borderColor: palette.primary.main,
        borderWidth: 2,
        pointRadius: 0,
        data: chartData,
      },
      {
        label: t("estimation"),
        fill: false,
        lineTension: 0.5,
        borderDash: [10, 2],
        pointRadius: 0,
        borderColor: palette.gray.lighter,
        borderWidth: 2,
        data: estimationData,
      },
    ],
  };

  const clicksToResetZoom = 2;

  const handleChartClick = (e) => {
    if (e.detail === clicksToResetZoom) {
      chartRef.current.chartInstance.resetZoom();
    }
  };

  const handleSaveWebhooks = () => {
    const webhook_ids = selectedWebhooks.map((w) => w.id);
    let parsedPayload = null;
    if (webhookPayloadValues.trim() !== "") {
      try {
        parsedPayload = JSON.parse(webhookPayloadValues);
      } catch (e) {
        setJsonError(t("invalid_json"));
        return;
      }
    }
    updateMaintenance({
      id: props.details.id,
      webhook_ids,
      webhook_payload_values: parsedPayload,
    }).then(() => {
      setEditWebhooksOpen(false);
      if (props.onRefresh) props.onRefresh();
    });
  };

  const minValue = () => {
    const closestStep = Math.floor(_.last(details.burn_rates)?.value / 20) * 20;
    return _.min([0, closestStep]);
  };

  const maxDate = () => {
    const now = moment();
    const recommendedDate = moment(details.recommended_date).isValid()
      ? moment(details.recommended_date)
      : now;
    return moment.max(recommendedDate, now);
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    legend: {
      display: false,
    },
    animation: {
      duration: 0,
    },
    onClick: handleChartClick,
    onHover: (e) => (e.target.style.cursor = "zoom-in"),
    annotation: {
      annotations: [
        {
          type: "line",
          mode: "horizontal",
          scaleID: "y-axis-0",
          lineTension: 0.5,
          borderDash: [10, 2],
          pointRadius: 0,
          value: details.cutoff_value,
          borderColor: palette.status.error.main,
          borderWidth: 2,
          label: {
            enabled: false,
            content: t("cutoff_value"),
          },
        },
        {
          drawTime: "beforeDatasetsDraw",
          type: "box",
          xScaleID: "top-x-axis",
          backgroundColor: pattern.draw("diagonal", "rgb(234, 241, 247, 0.8)"),
          borderColor: "rgb(234, 241, 247)",
          xMin: details?.last_performed_at,
          xMax: details.burn_rates?.[0]?.system_time,
        },
      ],
    },
    scales: {
      xAxes: [
        {
          id: "top-x-axis",
          position: "bottom",
          type: "time",
          distribution: "linear",
          gridLines: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            min: details.last_performed_at,
            max: maxDate(),
            beginAtZero: true,
            padding: 20,
            fontColor: palette.text.secondary,
            maxTicksLimit: 12,
          },
          time: {
            displayFormats: {
              millisecond: "MM/DD/YY h:mm a",
              second: "MM/DD/YY h:mm a",
              day: "MM/DD/YY",
            },
          },
        },
      ],
      yAxes: [
        {
          ticks: {
            min: minValue(),
            max: 100,
          },
        },
      ],
    },
    plugins: {
      zoom: {
        zoom: {
          enabled: true,
          mode: "x",
          drag: true,
        },
      },
    },
  };

  const formatPredictionAccuracy = (predictionAccuracy) => {
    const values = {
      on_time: t("right_on_time"),
      early: t("too_early"),
      late: t("too_late"),
    };
    return values[predictionAccuracy] || "--";
  };

  const displaySnoozeButton = () => {
    if (status === "Overdue") {
      return !details.snoozed_until || moment().isAfter(moment(details.snoozed_until));
    }
    return false;
  };

  const displayUnsnoozeButton = () => {
    if (details.snoozed_until) {
      return moment().isBefore(moment(details.snoozed_until));
    }
    return false;
  };

  return (
    <Grid item sm={12} className={classes.root}>
      <Card className={classes.card}>
        {details.last_performed_at && details.recommended_date ? (
          <CardHeader
            title={details.name}
            className={classes.header}
            subheader={
              <div className={classes.header}>
                <Typography gutterBottom variant="subtitle1" className={classes.gutterRight}>
                  {t("last")} {_.capitalize(details.verbs.noun)}:{" "}
                  {moment(details.last_performed_at).format("ll")}
                </Typography>
                <Typography gutterBottom variant="subtitle1">
                  {t("suggested")} {_.capitalize(details.verbs.noun)}:{" "}
                  {moment(details.recommended_date).format("ll")}
                </Typography>
              </div>
            }
            action={
              <>
                {(isSuperUser() || isOEMSuperUser()) && (
                  <>
                    <Button
                      color="default"
                      variant="contained"
                      size="medium"
                      onClick={() => setEditWebhooksOpen(true)}
                      style={{ marginRight: "10px" }}
                    >
                      Edit Webhooks
                    </Button>
                    <ButtonConfirm
                      color="primary"
                      variant="contained"
                      size="medium"
                      onConfirm={() => props.onDeleteConfirm(details.id)}
                      buttonText={t("delete")}
                      dialogLabel={t("delete_confirmation", { name: details.name })}
                      dialogText={t("delete_maintenance_warning")}
                    />
                    <Button
                      style={{ marginLeft: "10px" }}
                      color="secondary"
                      variant="contained"
                      size="medium"
                      onClick={props.onCheckClick}
                    >
                      {t("mark_as_done")}
                    </Button>
                    {displaySnoozeButton() && (
                      <Button
                        style={{ marginLeft: "10px" }}
                        color="default"
                        variant="contained"
                        size="medium"
                        onClick={props.onSnoozeConfirm}
                      >
                        {t("snooze")}
                      </Button>
                    )}
                    {displayUnsnoozeButton() && (
                      <ButtonConfirm
                        style={{ marginLeft: "10px" }}
                        color="secondary"
                        variant="contained"
                        size="medium"
                        onConfirm={props.onUnsnoozeConfirm}
                        buttonText={t("unsnooze")}
                        dialogLabel={t("unsnooze_confirmation", { name: details.name })}
                        dialogText={t("are_you_sure")}
                      />
                    )}
                  </>
                )}
              </>
            }
          />
        ) : (
          <CardHeader
            title={details.name}
            action={
              (isSuperUser() || isOEMSuperUser()) && (
                <>
                  <Button
                    color="default"
                    variant="contained"
                    size="medium"
                    onClick={() => setEditWebhooksOpen(true)}
                    style={{ marginRight: "10px" }}
                  >
                    Edit Webhooks
                  </Button>
                  <ButtonConfirm
                    color="primary"
                    variant="contained"
                    size="medium"
                    onConfirm={() => props.onDeleteConfirm(details.id)}
                    buttonText={t("delete")}
                    dialogLabel={`Delete "${details.name}"?`}
                    dialogText={t("delete_maintenance_warning")}
                  />
                </>
              )
            }
          />
        )}

        <Divider />
        <CardContent>
          {!_.isEmpty(details.history) ? (
            <div className={classes.chartContainer}>
              <Typography gutterBottom variant="h5">
                {t("burndown_chart")}
              </Typography>
              <Line
                ref={chartRef}
                data={data}
                options={options}
                plugins={[ChartAnnotation, ChartZoom]}
              />
              <Divider className={classes.divider} />
            </div>
          ) : (
            <MaintenanceOnboarding
              id={details.id}
              name={details.name}
              target={details.target}
              verbs={details.verbs}
              actions={details.actions}
              hidePrediction={_.isEmpty(details.history)}
              onClick={props.onMarkMaintenanceAsDone}
              lastPerformedAt={_.max(details.history?.map((h) => h.performed_at))}
            />
          )}
          <div className={classes.historyContainer}>
            <Typography gutterBottom variant="h5">
              {_.capitalize(details.verbs.noun)} {t("history")}
            </Typography>
            {!_.isEmpty(details.history) ? (
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small" aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("date")}</TableCell>
                      <TableCell>{t("status")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.history.map((history, key) => (
                      <TableRow key={key}>
                        <TableCell>{moment(history.performed_at).format("ll")}</TableCell>
                        <TableCell>{formatPredictionAccuracy(history.prediction_accuracy)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="subtitle1">
                {t("no_history", { noun: details.verbs.noun })}
              </Typography>
            )}
            <br />
            <Typography gutterBottom variant="h5">
              {t("snooze_history")}
            </Typography>
            {!_.isEmpty(details.snooze_histories) ? (
              <TableContainer component={Paper}>
                <Table className={classes.table} size="small" aria-label="a dense table">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("snooze_start")}</TableCell>
                      <TableCell>{t("snoozed_until")}</TableCell>
                      <TableCell>{t("reason_for_snoozing_caps")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(details.snooze_histories).map(([snoozeDate, [endDate, reason]], key) => (
                      <TableRow key={key}>
                        <TableCell>{moment(snoozeDate).format("ll")}</TableCell>
                        <TableCell>{moment(endDate).format("ll")}</TableCell>
                        <TableCell>{reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="subtitle1">{t("no_snooze_history")}</Typography>
            )}
          </div>
        </CardContent>
      </Card>
      <Dialog open={editWebhooksOpen} onClose={() => setEditWebhooksOpen(false)}>
        <DialogTitle>Edit Webhooks</DialogTitle>
        <DialogContent>
          <Autocomplete
            multiple
            options={webhooksList}
            getOptionLabel={(option) => option.webhook_name}
            value={selectedWebhooks}
            onChange={(event, newValue) => setSelectedWebhooks(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                label="Webhooks"
                placeholder="Select webhooks"
              />
            )}
          />
          <TextField
            label={t("webhook_payload_values")}
            multiline
            rows={4}
            variant="outlined"
            fullWidth
            placeholder='{"key": "value"}'
            value={webhookPayloadValues}
            onChange={(e) => {
              const val = e.target.value;
              setWebhookPayloadValues(val);
              try {
                if (val.trim() === "") setJsonError("");
                else {
                  JSON.parse(val);
                  setJsonError("");
                }
              } catch (e) {
                setJsonError(t("invalid_json"));
              }
            }}
            error={!!jsonError}
            helperText={jsonError || t("webhook_payload_values_help")}
            style={{ marginTop: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditWebhooksOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveWebhooks} color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

MaintenanceDetail.propTypes = {
  details: PropTypes.object,
  onCheckClick: PropTypes.func,
  onDeleteConfirm: PropTypes.func,
  onSnoozeConfirm: PropTypes.func,
  onUnsnoozeConfirm: PropTypes.func,
  onMarkMaintenanceAsDone: PropTypes.func,
  getMaintenanceStatus: PropTypes.func,
  onRefresh: PropTypes.func,
};

export default MaintenanceDetail;